/**
 * The paid plan's monthly allocation, as the page sees it (2026-10-11).
 *
 * Each paid month adds the plan's own unit of pr0 per seat (one Prest0n
 * employee's month on edition 2026-11, 450 at launch; 2,200 a Prest0n on a plan
 * signed on edition 2026-08, src/config/planTiers.ts), good for that month and
 * the next, used before the wallet. The server keeps the allocation and writes
 * this view into accounts/{accountId}/wallet/displaySummary.plan_allocation on
 * every grant and every turn (functions/src/billing/planAllocation.ts,
 * planAllocationDisplay).
 *
 * The page recomputes what is usable NOW from the lots at its own clock, so a
 * prepaid year's next month, or an expired one, shows correctly without waiting
 * for the next write. usableAllocationPr0 is the twin of the server's
 * availableAllocationPr0, pinned equal by tests/billing/planAllocationTwin.test.ts.
 */
import { PUBLISHED_EDITION, editionOfTier, isLadderTier, unitOfTier } from './planTiers';
import { seatCount, seatWordsFor } from './planCardWords';
import { PR0_PER_EMPLOYEE } from './employeeUnit.generated';

export interface PlanAllocationLotView {
  remaining_pr0: number;
  available_from_sec: number;
  expires_at_sec: number;
}

export interface PlanAllocationView {
  /** A full month for the current team, in pr0. */
  monthlyPr0: number;
  lots: PlanAllocationLotView[];
  ended: boolean;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** The stored view, re-validated; null when absent or malformed. */
export function parsePlanAllocationView(raw: unknown): PlanAllocationView | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const lots: PlanAllocationLotView[] = [];
  for (const l of Array.isArray(r.lots) ? r.lots : []) {
    if (!l || typeof l !== 'object') continue;
    const x = l as Record<string, unknown>;
    const remaining = num(x.remaining_pr0);
    const from = num(x.available_from_sec);
    const until = num(x.expires_at_sec);
    if (remaining === null || from === null || until === null) continue;
    lots.push({ remaining_pr0: Math.max(0, remaining), available_from_sec: from, expires_at_sec: until });
  }
  return { monthlyPr0: Math.max(0, num(r.monthly_pr0) ?? 0), lots, ended: r.ended === true };
}

/** pr0 usable at `nowMs` (0 once the subscription has ended). */
export function usableAllocationPr0(view: PlanAllocationView | null | undefined, nowMs: number): number {
  if (!view || view.ended) return 0;
  const nowSec = Math.floor(nowMs / 1000);
  let sum = 0;
  for (const lot of view.lots) {
    if (lot.available_from_sec <= nowSec && nowSec < lot.expires_at_sec) sum += lot.remaining_pr0;
  }
  return Math.round(sum * 10_000) / 10_000;
}

/** The soonest a usable lot with pr0 left expires (epoch seconds), or null. */
export function nextAllocationExpirySec(view: PlanAllocationView | null | undefined, nowMs: number): number | null {
  if (!view || view.ended) return null;
  const nowSec = Math.floor(nowMs / 1000);
  let soonest: number | null = null;
  for (const lot of view.lots) {
    if (lot.remaining_pr0 <= 0 || lot.available_from_sec > nowSec || nowSec >= lot.expires_at_sec) continue;
    if (soonest === null || lot.expires_at_sec < soonest) soonest = lot.expires_at_sec;
  }
  return soonest;
}

/**
 * The plan card's allocation row, the same words in both designs of
 * /subscription (and /wallet): what is usable now, and the monthly amount with
 * its one-month rollover.
 */
export const PLAN_ALLOCATION_WORDS = {
  rowLabel: 'included work',
  rowValue: (left: string, monthly: string, unit: string) =>
    `${left} ${unit} to use now · ${monthly} ${unit} arrive each month and stay good through the next`,
  ended: 'ended with the plan',
};

/** The wallet's work-units line, around its figure: "<lead> 1,350 pr0 <tail>". */
export interface WorkUnitsLine {
  lead: string;
  pr0: number;
  tail: string;
}

/**
 * What the wallet says a month of plan work is (both designs of /wallet draw it): the live plan's OWN unit × its seats
 * ("your plan includes 1,350 pr0 a month for 3 Prest0n employees"; a plan signed on edition 2026-08 still says its
 * 2,200 a seat). The seats and tier come from the billing answer the subscription and pricing pages cached; they are
 * used only while they agree with the server's own month for the plan (the allocation's `monthlyPr0`, seats × unit),
 * so a stale answer never names another team. With a plan whose team is not known here, the server's month alone; with
 * no plan, what a committed hire includes on the published ladder: one Prest0n employee's month.
 */
export function workUnitsLine(f: { tier?: string | null; seats?: number | null; monthlyPr0?: number | null }): WorkUnitsLine {
  const monthly = typeof f.monthlyPr0 === 'number' && Number.isFinite(f.monthlyPr0) && f.monthlyPr0 > 0 ? f.monthlyPr0 : null;
  const seats = typeof f.seats === 'number' && Number.isInteger(f.seats) && f.seats > 0 ? f.seats : null;
  const unit = isLadderTier(f.tier) ? unitOfTier(f.tier) : null;
  if (unit !== null && seats !== null && (monthly === null || monthly === unit * seats)) {
    const edition = editionOfTier(f.tier);
    const w = seatWordsFor(edition);
    const each = edition === PUBLISHED_EDITION ? '' : `, ${unit.toLocaleString('en-US')} each`;
    return { lead: 'your plan includes', pr0: unit * seats, tail: `a month for ${seatCount(seats, w)}${each}.` };
  }
  if (monthly !== null) return { lead: 'your plan includes', pr0: monthly, tail: 'a month.' };
  return { lead: 'committed hires include', pr0: PR0_PER_EMPLOYEE, tail: `a month for each ${seatWordsFor(PUBLISHED_EDITION).one}.` };
}
